const STRIPE_SDK_URL = 'https://js.stripe.com/v3/';
const DEFAULT_SDK_LOAD_TIMEOUT_MS = 20000;

/** @type {Promise<unknown>|null} */
let stripeSdkPromise = null;

/** @type {Map<string, Promise<unknown>>} */
const paypalSdkPromises = new Map();

/** @type {string|null} Last successfully loaded PayPal SDK src (for param mismatch). */
let loadedPayPalSdkSrc = null;

/**
 * Test-only: clear in-flight SDK promises between cases.
 */
export function resetPaymentSdkLoaderStateForTests() {
	stripeSdkPromise = null;
	paypalSdkPromises.clear();
	loadedPayPalSdkSrc = null;
}

/**
 * Read the active document CSP nonce from an already-allowed element.
 * Prefer the `.nonce` IDL (browsers may clear the attribute after parse).
 */
export function readDocumentCspNonce(doc = document) {
	const candidates = doc.querySelectorAll('script[nonce], style[nonce], link[nonce], [nonce]');

	for (const el of candidates) {
		const idlNonce = el.nonce;
		if (typeof idlNonce === 'string' && idlNonce !== '') {
			return idlNonce;
		}

		const attrNonce = el.getAttribute?.('nonce');
		if (typeof attrNonce === 'string' && attrNonce !== '') {
			return attrNonce;
		}
	}

	return '';
}

function applyCspNonce(script, nonce, { paypal = false } = {}) {
	if (!nonce) {
		return;
	}

	script.nonce = nonce;
	script.setAttribute('nonce', nonce);

	if (paypal) {
		script.setAttribute('data-csp-nonce', nonce);
	}
}

function appendScript(doc, script) {
	(doc.head || doc.documentElement).appendChild(script);
}

function removeScript(script) {
	if (!script) {
		return;
	}

	if (typeof script.remove === 'function') {
		script.remove();
		return;
	}

	script.parentNode?.removeChild?.(script);
}

function scriptSrcOf(script) {
	return script?.getAttribute?.('src') || script?.src || '';
}

/**
 * Compare PayPal SDK query params that affect button/vault behavior.
 */
export function paypalSdkSrcMatches(existingSrc, desiredSrc) {
	if (!existingSrc || !desiredSrc) {
		return false;
	}

	try {
		const existing = new URL(existingSrc, 'https://www.paypal.com');
		const desired = new URL(desiredSrc, 'https://www.paypal.com');

		if (`${existing.origin}${existing.pathname}` !== `${desired.origin}${desired.pathname}`) {
			return false;
		}

		for (const key of ['client-id', 'vault', 'currency']) {
			const left = existing.searchParams.get(key) || '';
			const right = desired.searchParams.get(key) || '';
			if (left !== right) {
				return false;
			}
		}

		return true;
	} catch {
		return false;
	}
}

function settleWithTimeout({ loadTimeoutMs, onTimeout }) {
	if (!Number.isFinite(loadTimeoutMs) || loadTimeoutMs <= 0) {
		return () => {};
	}

	const timer = setTimeout(() => {
		onTimeout();
	}, loadTimeoutMs);

	return () => {
		clearTimeout(timer);
	};
}

/**
 * Wait for an existing script's load or error (or timeout). Resolves once
 * the tag has finished attempting to execute — even if the global is missing.
 */
function waitForScriptSettle(script, { loadTimeoutMs } = {}) {
	return new Promise((resolve) => {
		let done = false;
		const finish = (reason) => {
			if (done) {
				return;
			}
			done = true;
			clearTimer();
			resolve(reason);
		};

		const clearTimer = settleWithTimeout({
			loadTimeoutMs,
			onTimeout: () => finish('timeout'),
		});

		// complete === true means load already fired (HTMLScriptElement).
		if (script.complete === true || script.dataset?.sdkLoadSettled === '1') {
			finish('already-settled');
			return;
		}

		script.addEventListener('load', () => {
			if (script.dataset) {
				script.dataset.sdkLoadSettled = '1';
			}
			finish('load');
		}, { once: true });
		script.addEventListener('error', () => {
			if (script.dataset) {
				script.dataset.sdkLoadSettled = '1';
			}
			finish('error');
		}, { once: true });
	});
}

/**
 * Tear down a loaded PayPal SDK so a different vault/currency build can load.
 */
export function teardownPayPalSdk({
	doc = document,
	clearPayPal = (win) => {
		try {
			delete win.paypal;
		} catch {
			win.paypal = undefined;
		}
		// PayPal Buttons leave zoid iframe state on window; clear common keys.
		try {
			delete win.__zoid__;
		} catch {
			win.__zoid__ = undefined;
		}
		try {
			delete win['paypal-js'];
		} catch {
			// ignore
		}
	},
	getWindow = () => (typeof window !== 'undefined' ? window : undefined),
} = {}) {
	for (const script of [...doc.querySelectorAll('script[src*="paypal.com/sdk/js"]')]) {
		removeScript(script);
	}

	const win = getWindow();
	if (win) {
		clearPayPal(win);
	}

	paypalSdkPromises.clear();
	loadedPayPalSdkSrc = null;
}

/**
 * Idempotently load Stripe.js when window.Stripe is missing (e.g. Livewire
 * / wire:navigate rendered the payment step without @push('scripts')).
 *
 * If a script tag is already in the document, await its load/error first.
 * Only replace it after it has settled and the Stripe global is still missing
 * — never yank a tag that is still downloading (double-load / race).
 */
export function ensureStripeSdk({
	doc = document,
	createElement = (tag) => doc.createElement(tag),
	getStripe = () => (typeof window !== 'undefined' ? window.Stripe : undefined),
	loadTimeoutMs = DEFAULT_SDK_LOAD_TIMEOUT_MS,
} = {}) {
	const existingStripe = getStripe();
	if (typeof existingStripe !== 'undefined') {
		return Promise.resolve(existingStripe);
	}

	if (stripeSdkPromise) {
		return stripeSdkPromise;
	}

	stripeSdkPromise = (async () => {
		const existing = doc.querySelector(`script[src="${STRIPE_SDK_URL}"]`);
		if (existing) {
			await waitForScriptSettle(existing, { loadTimeoutMs });
			const afterWait = getStripe();
			if (typeof afterWait !== 'undefined') {
				stripeSdkPromise = null;
				return afterWait;
			}
			// Settled without a global (CSP blocked execution, empty body, etc.).
			removeScript(existing);
		}

		return new Promise((resolve, reject) => {
			let settled = false;
			let clearTimer = () => {};

			const finishOk = () => {
				if (settled) {
					return;
				}
				const stripe = getStripe();
				if (typeof stripe === 'undefined') {
					settled = true;
					clearTimer();
					stripeSdkPromise = null;
					reject(new Error('Stripe SDK loaded without window.Stripe'));
					return;
				}
				settled = true;
				clearTimer();
				stripeSdkPromise = null;
				// Keep stripeSdkPromise cleared so a later missing-global can retry;
				// callers that still have Stripe short-circuit above.
				resolve(stripe);
			};

			const finishErr = (message = 'Stripe SDK failed to load') => {
				if (settled) {
					return;
				}
				settled = true;
				clearTimer();
				stripeSdkPromise = null;
				reject(new Error(message));
			};

			clearTimer = settleWithTimeout({
				loadTimeoutMs,
				onTimeout: () => finishErr('Stripe SDK load timed out'),
			});

			const script = createElement('script');
			script.src = STRIPE_SDK_URL;
			applyCspNonce(script, readDocumentCspNonce(doc));
			script.addEventListener('load', () => {
				if (script.dataset) {
					script.dataset.sdkLoadSettled = '1';
				}
				finishOk();
			});
			script.addEventListener('error', () => {
				if (script.dataset) {
					script.dataset.sdkLoadSettled = '1';
				}
				finishErr('Stripe SDK failed to load');
			});
			appendScript(doc, script);
		});
	})();

	// Hold the in-flight promise for concurrent callers; clear when done.
	stripeSdkPromise = stripeSdkPromise.finally(() => {
		// If resolved with Stripe, leave promise null so next ensure can re-check getStripe.
		// Concurrent callers already hold this same promise reference.
	}).then(
		(value) => {
			stripeSdkPromise = null;
			return value;
		},
		(error) => {
			stripeSdkPromise = null;
			throw error;
		},
	);

	return stripeSdkPromise;
}

export function paypalSdkSrc({ clientId, vault = false, currency = null } = {}) {
	if (!clientId) {
		return null;
	}

	const params = new URLSearchParams({ 'client-id': clientId });
	if (vault) {
		params.set('vault', 'true');
	}
	if (currency) {
		params.set('currency', String(currency).toUpperCase());
	}

	return `https://www.paypal.com/sdk/js?${params.toString()}`;
}

/**
 * Checkout, invoice pay, and credits modal use different PayPal button host ids.
 */
export function resolvePayPalButtonContainer(doc = document) {
	return doc.getElementById('paypal-button-container')
		|| doc.getElementById('paypal-invoice-button')
		|| doc.getElementById('paypal-credit-button')
		|| null;
}

/**
 * Resolve PayPal client-id from the button container, an existing SDK tag, or Livewire.
 */
export function resolvePayPalClientId(component = null, doc = document) {
	const container = resolvePayPalButtonContainer(doc);
	const fromDom =
		container?.getAttribute?.('data-paypal-client-id') ||
		container?.dataset?.paypalClientId ||
		null;
	if (fromDom) {
		return fromDom;
	}

	const existingScript = doc.querySelector('script[src*="paypal.com/sdk/js"]');
	const src = existingScript?.getAttribute?.('src') || existingScript?.src || '';
	if (src) {
		try {
			const id = new URL(src, 'https://www.paypal.com').searchParams.get('client-id');
			if (id) {
				return id;
			}
		} catch {
			// ignore malformed src
		}
	}

	const fromComponent =
		typeof component?.paypalClientId === 'string' && component.paypalClientId !== ''
			? component.paypalClientId
			: null;
	if (fromComponent) {
		return fromComponent;
	}

	const fromWire = component?.$wire?.get?.('paypalClientId');
	if (typeof fromWire === 'string' && fromWire !== '') {
		return fromWire;
	}

	return null;
}

export function resolvePayPalVault(doc = document) {
	const container = resolvePayPalButtonContainer(doc);
	const raw = container?.getAttribute?.('data-paypal-vault') || container?.dataset?.paypalVault;

	return raw === true || raw === 'true' || raw === '1';
}

export function resolvePayPalCurrency(doc = document) {
	const container = resolvePayPalButtonContainer(doc);
	const raw = container?.getAttribute?.('data-paypal-currency') || container?.dataset?.paypalCurrency;

	if (typeof raw === 'string' && raw.trim() !== '') {
		return raw.trim().toUpperCase();
	}

	return null;
}

function currentPayPalScriptSrc(doc) {
	const existing = doc.querySelector('script[src*="paypal.com/sdk/js"]');
	return existing ? scriptSrcOf(existing) : loadedPayPalSdkSrc;
}

/**
 * Idempotently load the PayPal JS SDK with nonce + data-csp-nonce for CSP.
 * Reuses window.paypal only when the loaded script's vault/currency/client-id
 * match the requested params; otherwise tears down and reloads.
 */
export function ensurePayPalSdk({
	clientId,
	vault = false,
	currency = null,
	doc = document,
	createElement = (tag) => doc.createElement(tag),
	getPayPal = () => (typeof window !== 'undefined' ? window.paypal : undefined),
	loadTimeoutMs = DEFAULT_SDK_LOAD_TIMEOUT_MS,
	teardown = teardownPayPalSdk,
} = {}) {
	if (!clientId) {
		return Promise.reject(new Error('PayPal client id missing'));
	}

	const src = paypalSdkSrc({ clientId, vault, currency });
	const existingSrc = currentPayPalScriptSrc(doc);
	const existingPayPal = getPayPal();

	if (typeof existingPayPal !== 'undefined') {
		if (existingSrc && paypalSdkSrcMatches(existingSrc, src)) {
			return Promise.resolve(existingPayPal);
		}
		// Global present but vault/currency/client-id mismatch (e.g. checkout
		// vault → soft-nav to credits currency). Tear down before reload.
		teardown({ doc });
	} else if (existingSrc && !paypalSdkSrcMatches(existingSrc, src)) {
		teardown({ doc });
	}

	if (paypalSdkPromises.has(src)) {
		return paypalSdkPromises.get(src);
	}

	const promise = (async () => {
		const matching = doc.querySelector(`script[src="${src}"]`);
		if (matching) {
			await waitForScriptSettle(matching, { loadTimeoutMs });
			const afterWait = getPayPal();
			if (typeof afterWait !== 'undefined' && paypalSdkSrcMatches(scriptSrcOf(matching), src)) {
				loadedPayPalSdkSrc = src;
				return afterWait;
			}
			removeScript(matching);
		}

		// Any other paypal SDK tag (wrong params) should already be gone via teardown.
		for (const leftover of [...doc.querySelectorAll('script[src*="paypal.com/sdk/js"]')]) {
			if (!paypalSdkSrcMatches(scriptSrcOf(leftover), src)) {
				removeScript(leftover);
			}
		}

		return new Promise((resolve, reject) => {
			let settled = false;
			let clearTimer = () => {};

			const finishOk = () => {
				if (settled) {
					return;
				}
				const paypal = getPayPal();
				if (typeof paypal === 'undefined') {
					settled = true;
					clearTimer();
					paypalSdkPromises.delete(src);
					reject(new Error('PayPal SDK loaded without window.paypal'));
					return;
				}
				settled = true;
				clearTimer();
				loadedPayPalSdkSrc = src;
				resolve(paypal);
			};

			const finishErr = (message = 'PayPal SDK failed to load') => {
				if (settled) {
					return;
				}
				settled = true;
				clearTimer();
				paypalSdkPromises.delete(src);
				reject(new Error(message));
			};

			clearTimer = settleWithTimeout({
				loadTimeoutMs,
				onTimeout: () => finishErr('PayPal SDK load timed out'),
			});

			const script = createElement('script');
			script.src = src;
			applyCspNonce(script, readDocumentCspNonce(doc), { paypal: true });
			script.addEventListener('load', () => {
				if (script.dataset) {
					script.dataset.sdkLoadSettled = '1';
				}
				finishOk();
			});
			script.addEventListener('error', () => {
				if (script.dataset) {
					script.dataset.sdkLoadSettled = '1';
				}
				finishErr('PayPal SDK failed to load');
			});
			appendScript(doc, script);
		});
	})().finally(() => {
		paypalSdkPromises.delete(src);
	});

	paypalSdkPromises.set(src, promise);

	return promise;
}

/**
 * Ensure Stripe / PayPal globals exist before mounting payment UI.
 * Safe to call repeatedly; does not change payment amounts or server logic.
 */
export async function ensureCheckoutPaymentSdks(component, doc = document) {
	const tasks = [];

	if (typeof window === 'undefined' || typeof window.Stripe === 'undefined') {
		tasks.push(
			ensureStripeSdk({ doc }).catch((error) => {
				component.stripeBlocked = true;
				throw error;
			}),
		);
	}

	const clientId = resolvePayPalClientId(component, doc);
	if (clientId) {
		const vault = resolvePayPalVault(doc);
		const currency = resolvePayPalCurrency(doc);
		const desired = paypalSdkSrc({ clientId, vault, currency });
		const existingSrc = currentPayPalScriptSrc(doc);
		const needsPayPal =
			typeof window === 'undefined'
			|| typeof window.paypal === 'undefined'
			|| !existingSrc
			|| !paypalSdkSrcMatches(existingSrc, desired);

		if (needsPayPal) {
			tasks.push(
				ensurePayPalSdk({
					clientId,
					vault,
					currency,
					doc,
				}).catch((error) => {
					component.paypalBlocked = true;
					throw error;
				}),
			);
		}
	}

	if (tasks.length === 0) {
		return;
	}

	await Promise.allSettled(tasks);
}
